import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight, Heart, HandHeart, Users, Lightbulb, GraduationCap, HeartPulse,
  Star, ChevronRight, Bird, Smile, Award, Quote, ArrowUpRight,
} from "lucide-react";
import { api } from "../lib/api";
import CampaignCard from "../components/CampaignCard";
import Seo from "../components/Seo";
import LaurelWreath from "../components/LaurelWreath";
import { directors } from "../data/directors";
import { logger } from "../lib/logger";

const LOGO_URL = "/logo.jpg";

const SERVICES = [
  { Icon: HandHeart, title: "Community Support", tone: "pink",
    desc: "We provide essential support to individuals and families in need.", to: "/campaigns?category=community" },
  { Icon: Heart, title: "Empowerment", tone: "gold",
    desc: "Helping people gain skills, confidence, and resources to build a better future.", to: "/campaigns?category=education" },
  { Icon: Users, title: "Youth Programs", tone: "pink",
    desc: "Inspiring and supporting young people to reach their full potential.", to: "/campaigns?category=education" },
  { Icon: HeartPulse, title: "Health & Wellbeing", tone: "gold",
    desc: "Promoting physical and mental wellbeing for a happier community.", to: "/campaigns?category=medical" },
];

const AVATARS = [
  "https://images.unsplash.com/photo-1494790108377-be9c29b29330?crop=entropy&cs=srgb&fm=jpg&w=80&h=80&q=80&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?crop=entropy&cs=srgb&fm=jpg&w=80&h=80&q=80&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?crop=entropy&cs=srgb&fm=jpg&w=80&h=80&q=80&auto=format&fit=crop",
];

// Lens of lives — a slowly rotating ring of "people helped" portraits behind the logo
const HELPED_PEOPLE = [
  "https://images.unsplash.com/photo-1545048702-79362596cdc9?crop=faces&cs=srgb&fm=jpg&w=160&h=160&q=80&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1517630800677-932d836ab680?crop=faces&cs=srgb&fm=jpg&w=160&h=160&q=80&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1503454537195-1dcabb73ffb9?crop=faces&cs=srgb&fm=jpg&w=160&h=160&q=80&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1531123897727-8f129e1688ce?crop=faces&cs=srgb&fm=jpg&w=160&h=160&q=80&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1597223557154-721c1cecc4b0?crop=faces&cs=srgb&fm=jpg&w=160&h=160&q=80&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1519340241574-2cec6aef0c01?crop=faces&cs=srgb&fm=jpg&w=160&h=160&q=80&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1591343395082-e120087004b4?crop=faces&cs=srgb&fm=jpg&w=160&h=160&q=80&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1604881991720-f91add269bed?crop=faces&cs=srgb&fm=jpg&w=160&h=160&q=80&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1605518216938-7c31b7b14ad0?crop=faces&cs=srgb&fm=jpg&w=160&h=160&q=80&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1554151228-14d9def656e4?crop=faces&cs=srgb&fm=jpg&w=160&h=160&q=80&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1542156822-6924d1a71ace?crop=faces&cs=srgb&fm=jpg&w=160&h=160&q=80&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1531259683007-016a7b628fc3?crop=faces&cs=srgb&fm=jpg&w=160&h=160&q=80&auto=format&fit=crop",
];

export default function Home() {
  const [stats, setStats] = useState({ total_raised: 0, active_campaigns: 0, families_helped: 0, unique_donors: 0 });
  const [featured, setFeatured] = useState([]);
  const [hoveredDirector, setHoveredDirector] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [s, c] = await Promise.all([
          api.get("/stats/public"),
          api.get("/campaigns", { params: { status: "approved", page_size: 6 } }),
        ]);
        setStats(s.data);
        setFeatured(c.data.items || []);
      } catch (e) {
        logger.warn("Home stats/featured fetch failed", e);
      }
    })();
  }, []);

  // Stats displayed in the magenta card (use defaults if DB is sparse)
  const impactStats = [
    { value: "2,500+", label: "Lives Impacted", Icon: Users },
    { value: "150+", label: "Volunteers", Icon: Heart },
    { value: "50+", label: "Programs", Icon: Star },
    { value: "10+", label: "Years of Service", Icon: Smile },
  ];

  return (
    <div>
      <Seo
        title="Building stronger communities together"
        description="Spes alit, vitam renovat — Hope nourishes, life renews. Dkarez supports, empowers and uplifts individuals and families in need."
      />

      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden bg-white depth-3d">
        {/* BIG pink wave from the right — full height swoosh, matches reference */}
        <svg
          className="absolute inset-y-0 right-0 h-full w-[55%] -z-10 pointer-events-none"
          viewBox="0 0 800 1000" preserveAspectRatio="xMaxYMid slice" aria-hidden="true"
        >
          <defs>
            <linearGradient id="r-wave-a" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#FDE9F1" />
              <stop offset="55%" stopColor="#F8B5CD" />
              <stop offset="100%" stopColor="#FF1F8C" />
            </linearGradient>
            <linearGradient id="r-wave-b" x1="0.2" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#FFC0D5" />
              <stop offset="100%" stopColor="#E91E63" />
            </linearGradient>
          </defs>
          {/* Outer soft pink layer */}
          <path d="M0,140 C160,60 360,40 540,140 C660,210 740,360 800,560 L800,1000 L0,1000 Z" fill="url(#r-wave-a)" opacity="0.5" />
          {/* Inner bold magenta curve */}
          <path d="M260,80 C380,140 500,250 620,380 C720,490 780,680 800,860 L800,1000 L120,1000 C150,800 130,600 170,440 C190,300 220,180 260,80 Z" fill="url(#r-wave-b)" opacity="0.95" />
        </svg>

        {/* Subtle pink hint on the LEFT side too (very light) */}
        <svg
          className="absolute bottom-0 left-0 w-[28%] h-[55%] -z-10 pointer-events-none"
          viewBox="0 0 400 500" preserveAspectRatio="xMinYMax slice" aria-hidden="true"
        >
          <path d="M0,500 C80,400 160,360 240,280 C320,200 360,140 400,80 L400,500 Z" fill="#FDE9F1" opacity="0.6" />
        </svg>

        {/* Tiny twinkling stars (subtle) */}
        <Star size={10} className="absolute top-40 left-[7%] text-brand-gold twinkle fill-current z-10" />
        <Star size={8} className="absolute top-72 left-[40%] text-brand-gold twinkle-2 fill-current z-10" />
        <Star size={9} className="absolute top-44 right-[44%] text-brand-gold twinkle-3 fill-current z-10" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pt-20 lg:pt-28 pb-32 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* LEFT */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="relative z-10"
          >
            <h1 className="font-display-bold text-5xl sm:text-6xl lg:text-7xl text-brand-wine leading-[1.04] font-extrabold tracking-tight">
              Building <span className="font-script text-brand-primary text-6xl sm:text-7xl lg:text-[5.5rem] font-normal italic align-baseline" style={{
                textShadow: "0 8px 30px rgba(233,30,99,0.18)",
                paddingRight: "0.15em",
              }}>Stronger</span><br />
              Communities <span className="font-script text-brand-primary text-6xl sm:text-7xl lg:text-[5.5rem] font-normal italic align-baseline" style={{
                textShadow: "0 8px 30px rgba(233,30,99,0.18)",
              }}>Together</span>
            </h1>
            <p className="mt-7 text-lg text-brand-muted leading-relaxed max-w-xl font-sans-ui">
              We support, empower, and uplift individuals and families in need. Together, we create
              hope, transform lives, and build a stronger future.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/register" className="btn-gold !py-4 !px-8 text-base font-semibold depth-shadow-magenta" data-testid="hero-get-help">
                <Heart size={18} strokeWidth={1.8} className="mr-2" fill="currentColor" />
                Get Help Now
              </Link>
              <Link to="/campaigns" className="inline-flex items-center justify-center px-8 py-4 rounded-full border-2 border-brand-primary text-brand-primary hover:bg-brand-primary-light transition-all font-semibold bg-white/80 backdrop-blur-sm" data-testid="hero-support-cause">
                <Users size={17} strokeWidth={2} className="mr-2" />
                Support Our Cause
              </Link>
            </div>

            <div className="mt-16 flex items-center gap-3">
              <Bird size={26} strokeWidth={1.6} className="text-brand-gold" style={{ transform: "rotate(-15deg)" }} />
              <p className="font-script text-brand-primary text-2xl sm:text-3xl" data-testid="hero-motto">
                Spes alit, vitam renovat
              </p>
            </div>
          </motion.div>

          {/* RIGHT — circular logo emblem flowing into the page */}
          <motion.div
            initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.2, ease: "easeOut" }}
            className="relative flex items-center justify-center min-h-[500px]"
          >
            {/* Layer 1: outermost slow rotating ring of dots */}
            <motion.div
              className="absolute w-[540px] h-[540px] pointer-events-none"
              animate={{ rotate: 360 }}
              transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
              aria-hidden="true"
            >
              {Array.from({ length: 16 }).map((_, i) => {
                const angle = (i * 360) / 16;
                return (
                  <span
                    key={`outer-dot-${angle}`}
                    className="absolute top-1/2 left-1/2 w-1.5 h-1.5 rounded-full bg-brand-gold/70"
                    style={{
                      transform: `rotate(${angle}deg) translate(260px) rotate(-${angle}deg)`,
                    }}
                  />
                );
              })}
            </motion.div>

            {/* Layer 2: inner counter-rotating ring with magenta dots */}
            <motion.div
              className="absolute w-[440px] h-[440px] pointer-events-none"
              animate={{ rotate: -360 }}
              transition={{ duration: 45, repeat: Infinity, ease: "linear" }}
              aria-hidden="true"
            >
              {Array.from({ length: 10 }).map((_, i) => {
                const angle = (i * 360) / 10;
                return (
                  <span
                    key={`inner-dot-${angle}`}
                    className="absolute top-1/2 left-1/2 w-2 h-2 rounded-full"
                    style={{
                      background: i % 2 === 0 ? "#E91E63" : "#FFB72E",
                      transform: `rotate(${angle}deg) translate(210px) rotate(-${angle}deg)`,
                      boxShadow: "0 0 8px currentColor",
                    }}
                  />
                );
              })}
            </motion.div>

            {/* Layer 3: pulsing breathing aura — slow, no longer heartbeat */}
            <motion.div
              className="absolute rounded-full halo-breathe"
              style={{
                width: 460, height: 460,
                background: "radial-gradient(circle, rgba(233,30,99,0.38) 0%, rgba(255,183,46,0.22) 35%, transparent 70%)",
                filter: "blur(40px)",
              }}
              aria-hidden="true"
            />

            {/* Layer 4: pulsing soft white halo right behind the logo */}
            <motion.div
              className="absolute rounded-full bg-white/40"
              style={{ width: 360, height: 360, filter: "blur(20px)" }}
              animate={{ scale: [1, 1.06, 1] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              aria-hidden="true"
            />

            {/* Layer 4.5: "Lens of lives" — rotating ring mixing directors (clickable) with
                community portraits. Hovering anywhere on the lens pauses rotation so users
                can comfortably interact. Hovering a director wraps the portrait in a Greek
                laurel wreath and reveals their testimonial card below the logo. */}
            <div
              className={`absolute ${hoveredDirector || hoveredDirector === false ? "" : ""}`}
              style={{ width: 500, height: 500 }}
              data-testid="hero-lens-of-lives"
            >
              <div
                className={`lens-rotate w-full h-full relative ${hoveredDirector ? "lens-paused" : ""}`}
                onMouseEnter={(e) => { e.currentTarget.classList.add("lens-paused"); }}
                onMouseLeave={(e) => { e.currentTarget.classList.remove("lens-paused"); setHoveredDirector(null); }}
              >
                {(() => {
                  const slots = [
                    { type: "director", data: directors[0] },
                    { type: "person", src: HELPED_PEOPLE[0] },
                    { type: "person", src: HELPED_PEOPLE[1] },
                    { type: "director", data: directors[1] },
                    { type: "person", src: HELPED_PEOPLE[2] },
                    { type: "person", src: HELPED_PEOPLE[3] },
                    { type: "director", data: directors[2] },
                    { type: "person", src: HELPED_PEOPLE[4] },
                    { type: "person", src: HELPED_PEOPLE[5] },
                    { type: "director", data: directors[3] },
                    { type: "person", src: HELPED_PEOPLE[6] },
                    { type: "person", src: HELPED_PEOPLE[7] },
                  ];
                  return slots.map((s, i) => {
                    const angle = (i * 360) / slots.length;
                    const isDirector = s.type === "director";
                    const isHovered = isDirector && hoveredDirector?.slug === s.data.slug;
                    return (
                      <div
                        key={isDirector ? `dir-${s.data.slug}` : `person-${i}-${s.src}`}
                        className="absolute top-1/2 left-1/2"
                        style={{
                          transform: `translate(-50%, -50%) rotate(${angle}deg) translate(0, -240px)`,
                        }}
                      >
                        <div className="lens-counter-rotate relative group">
                          {isDirector ? (
                            <Link
                              to={`/team/${s.data.slug}`}
                              data-testid={`lens-director-${s.data.slug}`}
                              onMouseEnter={() => setHoveredDirector(s.data)}
                              onMouseLeave={() => setHoveredDirector((cur) => (cur?.slug === s.data.slug ? null : cur))}
                              onFocus={() => setHoveredDirector(s.data)}
                              onBlur={() => setHoveredDirector((cur) => (cur?.slug === s.data.slug ? null : cur))}
                              className="relative block w-14 h-14 sm:w-[62px] sm:h-[62px] rounded-full overflow-visible cursor-pointer"
                              title={`${s.data.name} — view profile`}
                            >
                              {/* Greek laurel wreath — appears on hover, scales in */}
                              <span
                                className={`absolute inset-0 -m-7 transition-all duration-500 ease-out ${
                                  isHovered ? "opacity-100 scale-110 rotate-0" : "opacity-0 scale-75 -rotate-12"
                                }`}
                                style={{ pointerEvents: "none" }}
                                aria-hidden="true"
                              >
                                <LaurelWreath
                                  className="w-full h-full drop-shadow-[0_4px_18px_rgba(255,183,46,0.7)]"
                                  color="#FFD16A"
                                  strokeColor="#A02157"
                                />
                              </span>

                              {/* Portrait */}
                              <span className="absolute inset-0 rounded-full overflow-hidden ring-[3px] ring-brand-gold shadow-[0_8px_22px_-4px_rgba(255,183,46,0.6)] bg-white block transition-transform duration-300 group-hover:scale-110">
                                <img
                                  src={s.data.photo}
                                  alt={s.data.name}
                                  loading="lazy"
                                  className="w-full h-full object-cover"
                                />
                              </span>
                            </Link>
                          ) : (
                            <div className="w-12 h-12 sm:w-[54px] sm:h-[54px] rounded-full overflow-hidden ring-2 ring-white shadow-[0_6px_18px_-4px_rgba(160,33,87,0.45)] bg-white pointer-events-none">
                              <img
                                src={s.src}
                                alt=""
                                loading="lazy"
                                className="w-full h-full object-cover"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>

            {/* Interactive testimonial wall — slides in below logo on director hover */}
            <AnimatePresence mode="wait">
              {hoveredDirector && (
                <motion.div
                  key={hoveredDirector.slug}
                  initial={{ opacity: 0, y: 24, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 16, scale: 0.96 }}
                  transition={{ duration: 0.35, ease: "easeOut" }}
                  className="absolute z-30 left-1/2 -translate-x-1/2 bottom-[-30px] sm:bottom-[-10px] w-[min(440px,92%)]"
                  data-testid={`testimonial-${hoveredDirector.slug}`}
                >
                  <Link
                    to={`/team/${hoveredDirector.slug}`}
                    className="glass-card rounded-2xl p-5 sm:p-6 depth-shadow block group hover:-translate-y-0.5 transition-transform"
                  >
                    <div className="flex items-start gap-4">
                      <span className="shrink-0 w-14 h-14 rounded-full overflow-hidden ring-2 ring-brand-gold">
                        <img src={hoveredDirector.photo} alt={hoveredDirector.name} className="w-full h-full object-cover" />
                      </span>
                      <div className="flex-1 min-w-0">
                        <span className="text-[0.65rem] uppercase tracking-[0.18em] font-semibold text-brand-primary">
                          {hoveredDirector.title}
                        </span>
                        <h4 className="font-display-bold text-lg leading-tight text-brand-text truncate">
                          {hoveredDirector.name}
                        </h4>
                      </div>
                      <Quote size={22} className="text-brand-gold shrink-0" strokeWidth={1.6} />
                    </div>
                    <p className="mt-3 text-sm leading-relaxed text-brand-muted italic line-clamp-3">
                      "{hoveredDirector.vision}"
                    </p>
                    <div className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-brand-primary group-hover:gap-2.5 transition-all">
                      Read full profile
                      <ArrowUpRight size={14} strokeWidth={2} className="transition-transform group-hover:rotate-45" />
                    </div>
                  </Link>
                </motion.div>
              )}
            </AnimatePresence>

            {/* The logo — clipped to circle, no heartbeat, just gentle floating + 3D tilt on hover */}
            <motion.div
              className="relative z-10 rounded-full overflow-hidden"
              style={{
                width: 380, height: 380,
                filter: "drop-shadow(0 30px 60px rgba(160, 33, 87, 0.35))",
                transformStyle: "preserve-3d",
              }}
              animate={{ y: [0, -14, 0], rotateZ: [-1.5, 1.5, -1.5] }}
              transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
              whileHover={{
                scale: 1.05,
                rotateY: 8,
                rotateX: -6,
                transition: { duration: 0.5, ease: "easeOut" },
              }}
            >
              <img
                src={LOGO_URL}
                alt="Dkarez Community Support CIC"
                className="w-full h-full object-cover"
                style={{ mixBlendMode: "multiply" }}
              />
            </motion.div>

            {/* Sparkles that orbit asymmetrically */}
            <motion.div
              className="absolute pointer-events-none"
              animate={{ rotate: 360 }}
              transition={{ duration: 22, repeat: Infinity, ease: "linear" }}
              aria-hidden="true"
            >
              <div className="relative w-[480px] h-[480px] pointer-events-none">
                <Star size={20} className="absolute top-2 left-1/2 -translate-x-1/2 text-brand-gold twinkle fill-current" />
                <Star size={14} className="absolute bottom-6 right-12 text-brand-gold twinkle-2 fill-current" />
                <Star size={18} className="absolute top-1/3 -left-2 text-brand-primary twinkle-3 fill-current" />
                <Star size={12} className="absolute bottom-1/3 -right-2 text-brand-gold twinkle fill-current" />
              </div>
            </motion.div>

            {/* A second set of orbiting sparkles in opposite direction */}
            <motion.div
              className="absolute pointer-events-none"
              animate={{ rotate: -360 }}
              transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
              aria-hidden="true"
            >
              <div className="relative w-[560px] h-[560px] pointer-events-none">
                <Heart size={14} className="absolute top-10 right-12 text-brand-primary twinkle-2 fill-current opacity-75" />
                <Heart size={11} className="absolute bottom-14 left-14 text-brand-gold twinkle fill-current opacity-75" />
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ============ SERVICES + DONATE SIDEBAR ============ */}
      <section className="relative -mt-12 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* 4 service cards inside white wrapper (span 8) — glass + depth */}
            <div className="lg:col-span-8 glass-card rounded-3xl p-7 sm:p-9 depth-shadow">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-y-8 gap-x-7">
                {SERVICES.map((s, i) => (
                  <ServiceCard key={s.title} {...s} index={i} />
                ))}
              </div>
            </div>

            {/* Donate sidebar card — deep magenta with 6D shadow */}
            <motion.div
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.6 }}
              whileHover={{ y: -4, transition: { duration: 0.3 } }}
              className="lg:col-span-4 rounded-3xl p-8 text-white relative overflow-hidden flex flex-col justify-between depth-shadow-magenta"
              style={{ background: "linear-gradient(155deg, #A02157 0%, #E91E63 100%)" }}
              data-testid="home-make-difference"
            >
              <div className="gold-glow bg-brand-gold" style={{ width: 200, height: 200, top: -60, right: -40, opacity: 0.25 }} />
              <Star size={14} className="absolute top-6 right-8 text-brand-gold twinkle-2 fill-current" />
              <div className="relative">
                <p className="font-script text-brand-gold text-3xl mb-1">Today</p>
                <h3 className="font-display-bold text-2xl sm:text-[28px] font-bold leading-tight">Make a Difference</h3>
                <p className="text-white/90 text-sm mt-3 leading-relaxed">
                  Your support helps us continue our mission and change lives.
                </p>
              </div>
              <div className="relative mt-7">
                <div className="flex items-center gap-3">
                  <div className="flex -space-x-2.5">
                    {AVATARS.map((a) => (
                      <img key={a} src={a} alt="" loading="lazy"
                        className="w-10 h-10 rounded-full border-2 border-white object-cover"
                      />
                    ))}
                  </div>
                  <span className="text-sm text-white/95 font-semibold">+{Math.max(120, stats.unique_donors || 0)}</span>
                </div>
                <Link to="/campaigns" className="mt-6 inline-flex items-center justify-center w-full px-5 py-3.5 rounded-full bg-white text-brand-wine font-semibold hover:bg-brand-gold-light transition-colors" data-testid="sidebar-donate-now">
                  <Heart size={16} strokeWidth={1.8} className="mr-2 text-brand-primary" fill="#E91E63" />
                  Donate Now
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============ STATS + ABOUT — single magenta card with 6D depth ============ */}
      <section className="py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.7 }}
            className="rounded-3xl overflow-hidden relative grid grid-cols-1 lg:grid-cols-2 depth-shadow-magenta"
            style={{ background: "linear-gradient(120deg, #A02157 0%, #E91E63 100%)" }}
          >
            {/* LEFT — stats 4-column inside the magenta */}
            <div className="p-8 sm:p-12 grid grid-cols-2 sm:grid-cols-4 gap-y-10 gap-x-4 relative">
              {impactStats.map((s, i) => (
                <motion.div
                  key={s.label}
                  initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.06 }}
                  className="text-center text-white relative"
                  data-testid={`impact-stat-${s.label.toLowerCase().replace(/ /g, "-")}`}
                >
                  <s.Icon size={34} strokeWidth={1.6} className="text-brand-gold mx-auto" fill={s.label === "Programs" ? "currentColor" : "none"} />
                  <p className="font-display-bold text-3xl sm:text-4xl text-white mt-3 font-bold">{s.value}</p>
                  <p className="text-white/85 text-xs sm:text-sm mt-1 tracking-wide">{s.label}</p>
                  {i < 3 && <span className="hidden sm:block absolute top-1/2 -right-2 -translate-y-1/2 w-px h-16 bg-white/20" />}
                </motion.div>
              ))}
            </div>

            {/* RIGHT — About block (white bg) */}
            <div className="bg-white p-8 sm:p-12 relative">
              {/* Decorative gold dove + curved trail in the bottom right */}
              <svg className="absolute -right-6 bottom-2 w-44 h-44 pointer-events-none" viewBox="0 0 200 200" aria-hidden="true">
                <defs>
                  <linearGradient id="dove-trail" x1="0" y1="1" x2="1" y2="0">
                    <stop offset="0%" stopColor="#FFB72E" stopOpacity="0" />
                    <stop offset="100%" stopColor="#FFB72E" stopOpacity="0.9" />
                  </linearGradient>
                </defs>
                <path d="M10,180 Q80,140 130,90 T200,20" fill="none" stroke="url(#dove-trail)" strokeWidth="2.5"
                      strokeDasharray="6 8" strokeLinecap="round" />
              </svg>
              <motion.div
                className="absolute right-6 bottom-12 z-10"
                animate={{ y: [0, -8, 0], rotate: [-8, 4, -8] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                aria-hidden="true"
              >
                <Bird size={56} strokeWidth={1.2} className="text-brand-gold" />
              </motion.div>

              <h3 className="font-display-bold text-2xl sm:text-3xl text-brand-primary font-bold leading-snug">
                About <span className="font-script text-4xl sm:text-5xl text-brand-primary font-normal italic">DKarez</span> Community Support CIC
              </h3>
              <p className="text-brand-muted mt-5 leading-relaxed text-base font-sans-ui max-w-md">
                We are a community-driven organization committed to creating positive change. Our goal is to
                support those in need, empower individuals, and build a stronger, healthier, and more
                connected community.
              </p>
              <Link to="/about" className="mt-8 btn-gold !py-3.5 !px-7 inline-flex depth-shadow-magenta" data-testid="about-learn-more-btn">
                Learn More About Us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ============ FEATURED CAMPAIGNS ============ */}
      <section className="pb-24 max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
          <div className="max-w-xl">
            <span className="eyebrow font-display-bold">Live now</span>
            <h2 className="font-display-bold text-4xl sm:text-5xl mt-3 leading-tight text-brand-wine font-extrabold">
              Stories worth <span className="font-script text-brand-primary text-5xl sm:text-6xl font-normal italic">standing with</span>.
            </h2>
          </div>
          <Link to="/campaigns" className="btn-outline-gold !py-2.5" data-testid="home-view-all-btn">
            View all campaigns <ArrowRight size={16} strokeWidth={1.8} className="ml-2" />
          </Link>
        </div>

        {featured.length === 0 ? (
          <div className="premium-card p-16 text-center">
            <p className="font-display-bold text-2xl text-brand-wine font-bold">No live campaigns yet</p>
            <p className="text-brand-muted mt-2">Be the first to create one — your story could light the way.</p>
            <Link to="/register" className="btn-gold mt-6 inline-flex">Get started</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
            {featured.map((c, i) => <CampaignCard key={c.id} campaign={c} index={i} />)}
          </div>
        )}
      </section>
    </div>
  );
}


function ServiceCard({ Icon, title, desc, tone, to, index }) {
  const gradient = tone === "pink"
    ? "linear-gradient(160deg, #FF7AB0 0%, #E91E63 100%)"
    : "linear-gradient(160deg, #FFD466 0%, #FFB72E 100%)";
  const shadow = tone === "pink"
    ? "0 14px 26px -10px rgba(233, 30, 99, 0.5)"
    : "0 14px 26px -10px rgba(255, 183, 46, 0.5)";
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }} transition={{ duration: 0.45, delay: index * 0.06 }}
      className="flex items-start gap-5 group"
      data-testid={`service-${title.toLowerCase().replace(/[ &]+/g, "-")}`}
    >
      <div
        className="flex-shrink-0 w-[72px] h-[72px] rounded-full flex items-center justify-center transition-transform duration-500 group-hover:scale-110 group-hover:-translate-y-1"
        style={{ background: gradient, boxShadow: shadow }}
      >
        <Icon size={30} strokeWidth={1.8} className="text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="font-display-bold text-xl text-brand-primary font-bold">{title}</h4>
        <p className="text-sm text-brand-muted mt-2 leading-relaxed">{desc}</p>
        <Link to={to} className="mt-3 inline-flex items-center gap-1 text-brand-primary text-sm font-semibold hover:gap-2 transition-all">
          Learn More <ArrowRight size={13} strokeWidth={2} />
        </Link>
      </div>
    </motion.div>
  );
}
